import { useMemo } from 'react';
import type { components } from '@bookingdei/api-client/types';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { formatMoney } from '@bookingdei/i18n/format-money';
import { useLanguage } from '../../shell/language-context';
import { useSettings } from '../../shell/settings-context';

type OfferCard = components['schemas']['OfferCard'];
type Category = components['schemas']['Category'];

export interface OfferFormat {
  num: (n: number) => string;
  money: (minor: number) => string;
  /** "40% OFF" — the percentage the card advertises; "৳500 OFF" for a fixed discount with no price to work from. */
  off: (o: OfferCard) => string;
  /** Whether the card has a before/after price to show (a listing with no catalog price has none). */
  hasPrice: (o: OfferCard) => boolean;
  /** Day-granular expiry (FR-C19) and whether it is the urgent "ends today" case. */
  ends: (o: Pick<OfferCard, 'ends_in_days'>) => { label: string; urgent: boolean };
  /** "5h ago" since the offer went live (`starts_at`), for the just-added rail. */
  posted: (o: OfferCard) => string;
  bizName: (o: OfferCard) => string;
  deal: (o: OfferCard) => string;
  catLabel: (c: Category) => string;
}

// Every number and amount on the Offers page goes through the packages/i18n
// formatters (grouped digits, Bangla numerals under bn); the currency comes from the
// settings registry, never a literal (INF-1). Both prices arrive server-computed
// (API-52, DB-48) — nothing here derives a price.
export function useOfferFormat(): OfferFormat {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const symbol = settings?.currency?.symbol ?? settings?.currency?.code ?? '';
  const scale = settings?.currency?.minor_unit_scale ?? 100;

  return useMemo(() => {
    const num = (n: number) => formatNumber(lang, n);
    const money = (minor: number) => formatMoney(lang, minor, { currencySymbol: symbol, minorUnitScale: scale });
    const pct = (o: OfferCard) =>
      o.discount_kind === 'percent'
        ? o.discount_value
        : o.price_before_minor > 0
          ? Math.round(((o.price_before_minor - o.price_after_minor) / o.price_before_minor) * 100)
          : 0;
    const ends = (o: Pick<OfferCard, 'ends_in_days'>) => {
      const days = o.ends_in_days ?? 0;
      if (days === 0) return { label: t('dc52.ends_today'), urgent: true };
      if (days === 1) return { label: t('dc52.ends_tomorrow'), urgent: false };
      return { label: t('dc52.days_left', { n: num(days) }), urgent: false };
    };
    const posted = (o: OfferCard) => {
      const since = o.starts_at ? Date.now() - Date.parse(o.starts_at) : Number.NaN;
      if (!Number.isFinite(since)) return '';
      const hours = Math.floor(since / 3_600_000);
      if (hours <= 1) return t('dc52.posted_now');
      if (hours < 48) return t('dc52.posted_hours', { n: num(hours) });
      return t('dc52.posted_days', { n: num(Math.floor(hours / 24)) });
    };
    return {
      num,
      money,
      off: (o) =>
        o.discount_kind === 'fixed' && !(o.price_before_minor > 0) ? t('dc52.off_amount', { amount: money(o.discount_value) }) : t('dc52.off', { n: num(pct(o)) }),
      hasPrice: (o) => o.price_before_minor > o.price_after_minor && o.price_after_minor > 0,
      ends,
      posted,
      bizName: (o) => (lang === 'bn' && o.business.name_bn ? o.business.name_bn : o.business.name),
      deal: (o) => o.tagline ?? o.title,
      catLabel: (c) => (lang === 'bn' ? c.label_bn : c.label_en) ?? c.label_en,
    };
  }, [lang, symbol, scale, t]);
}
